import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TeamService } from './services/team.service';
import { SeoService } from './services/seo.service';
import { BreadcrumbsService } from './services/breadcrumbs.service';

export interface TeamMemberDetail {
  name: string;
  slug: string;
  role: string;
  department?: string;
  photo?: {
    url: string;
    alternativeText?: string;
  };
  thumbnailPhoto?: {
    url: string;
    alternativeText?: string;
  };
  shortBio: string;
  fullBio?: string;
  expertise?: string[];
  email?: string;
  phone?: string;
  linkedin?: string;
  whatsapp?: string;
  isActive: boolean;
  seoTitle?: string | null;
  seoDescription?: string | null;
  ogImageUrl?: string | null;
}

@Component({
  selector: 'app-team-profile',
  standalone: false,
  templateUrl: './team-profile.component.html',
  styleUrls: ['./team-profile.component.scss'],
})
export class TeamProfileComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  detail = signal<TeamMemberDetail | null>(null);
  isLoading = signal(true);
  errorMessage = signal('');

  private readonly destroy$ = new Subject<void>();
  private readonly jsonLdId = 'team-person';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private teamService: TeamService,
    private seoService: SeoService,
    private breadcrumbsService: BreadcrumbsService
  ) {}

  ngOnInit(): void {
    // Scroll to top when component loads - multiple approaches for iOS compatibility
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Additional scroll on next tick for iOS
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
    
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const slug = params.get('slug');
      // Clear any prior member's structured data immediately on slug change so a
      // stale Person card never advertises the wrong person during the fetch.
      this.seoService.removeJsonLd(this.jsonLdId);
      if (slug) {
        this.loadTeamMember(slug);
      } else {
        this.errorMessage.set('Geen teamlid geselecteerd');
        this.isLoading.set(false);
      }
    });
  }

  ngAfterViewInit(): void {
    // Delay to ensure content is rendered
    setTimeout(() => {
      this.setupScrollAnimations();
    }, 100);
  }

  ngOnDestroy(): void {
    this.seoService.removeJsonLd(this.jsonLdId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadTeamMember(slug: string): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.teamService.detail(slug).subscribe({
      next: (member: TeamMemberDetail) => {
        if (member) {
          this.detail.set(member);
          this.updateSEO();
          this.applyStructuredData();
          this.updateBreadcrumbs();

          // Trigger animations after data loads
          setTimeout(() => {
            this.setupScrollAnimations();
          }, 100);
        } else {
          this.errorMessage.set('Teamlid niet gevonden');
        }
        this.isLoading.set(false);
      },
      error: (error: any) => {
        console.error('Error loading team member:', error);
        this.errorMessage.set(
          'Er is een fout opgetreden bij het laden van het teamlid'
        );
        this.isLoading.set(false);
      },
    });
  }

  private updateSEO(): void {
    const detail = this.detail();
    if (!detail) return;

    // Editor-controlled META (BLA-6) with sensible fallbacks. SeoService.buildTitle
    // appends the "• Blauwtand" brand suffix (and de-dupes if the editor already
    // typed it), so we no longer hard-code the brand here.
    const title = detail.seoTitle?.trim() || `${detail.name} - ${detail.role}`;
    const description = detail.seoDescription?.trim() || detail.shortBio;
    const canonicalUrl = this.seoService.generateCanonical(
      `/over-blauwtand/team/${detail.slug}`
    );
    const imageUrl = detail.ogImageUrl || detail.photo?.url || '';

    this.seoService.updateSeo({
      title,
      description,
      canonicalUrl,
      imageUrl,
      ogType: 'profile',
    });
  }

  /**
   * Emit ProfilePage > Person structured data (Google's preferred shape for a
   * person bio page). Optional fields are omitted entirely when empty — emitting
   * `email: null` / `image: ''` would be invalid schema.org.
   */
  private applyStructuredData(): void {
    const detail = this.detail();
    if (!detail) return;

    const canonical = this.seoService.generateCanonical(
      `/over-blauwtand/team/${detail.slug}`
    );
    const image = detail.photo?.url || detail.ogImageUrl || '';
    const sameAs = [detail.linkedin].filter(
      (v): v is string => Boolean(v && v.trim())
    );

    const person: Record<string, unknown> = {
      '@type': 'Person',
      name: detail.name,
      jobTitle: detail.role,
      description: detail.shortBio,
      url: canonical,
      worksFor: {
        '@type': 'Organization',
        name: 'Blauwtand',
        url: 'https://blauwtand.com',
      },
      ...(image ? { image } : {}),
      ...(detail.email ? { email: detail.email } : {}),
      ...(detail.phone ? { telephone: detail.phone } : {}),
      ...(sameAs.length ? { sameAs } : {}),
    };

    this.seoService.addJsonLd(this.jsonLdId, {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: person,
    });
  }

  private updateBreadcrumbs(): void {
    const detail = this.detail();
    if (detail) {
      this.breadcrumbsService.setCurrentLeafLabel(detail.name);
      this.breadcrumbsService.insertBeforeLeaf('Team', '/over-blauwtand#team');
    }
  }

  private setupScrollAnimations(): void {
    const elements = document.querySelectorAll('.animate-on-scroll');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    elements.forEach((el) => observer.observe(el));
  }

  goBack(): void {
    this.router.navigate(['/about']);
  }
}
